import React, { createRef } from 'react';
import MapTrafficVolumeCacheComponent from 'app/components/dashboard/network/map/MapTrafficVolumeCache';
import { MapPendingEventsComponent } from 'app/components/dashboard/network/map/MapPendingEventsComponent';
import { SvgCommonDefs } from 'app/components/dashboard/network/flows/svg/SvgCommonDefs';
import { Flex } from 'app/components/common/Flex';
import { notification, Spin } from 'antd';
import { RootState } from 'app/redux/store';
import {
    getDashboardData,
    getDashboardDataForCurrentVersion,
    getDataVersion,
    setDataVersion,
    setDrawerOpen,
} from 'app/redux/features/dashboard/dashboard-slice';
import { setFullscreenMapSettings, setMapSettings } from 'app/redux/features/map/settings-slice';
import { connect, ConnectedProps } from 'react-redux';
import { Banner } from 'app/components/dashboard/global/Banner';
import { UserInfo } from 'app/components/dashboard/global/UserInfo';
import AppContext from 'app/common/context/AppContext';
import NetworkMonitoringMap from 'app/components/dashboard/network/NetworkMonitoringMap';
import './Dashboard.less';
import {
    DashboardDataRequest,
    ExtendedMonitoredTv,
    MilitaryZoneDto,
    MonitoredMeasure,
    NetworkDashboardData,
    NiaMeasureImpact,
    PolygonMeteoForecast,
    MonitoredRegulationOutlook,
    TrafficVolumeMeteoForecast,
} from 'app/model/SOL49';
import GlobalTimeSelectionPopup from 'app/components/dashboard/network/popup/GlobalTimeSelectionPopup';
import TimeUtils from 'app/common/util/TimeUtils';
import { MonitoredDaySelector } from 'app/components/dashboard/network/MonitoredDaySelector';
import { MonitoringPeriodSettings } from 'app/model/client/SOL49';
import {
    DashboardSettingsUpdateEvent,
    MapElementClickEvent,
    MonitoringPeriodSettingsChange,
    ShowModalEvent,
    ShowTvDetailEvent,
} from 'app/common/eventbus/Events';
import '../components/dashboard/network/NetworkMonitoringHeader.less';
import { DashboardModal } from 'app/components/panels/DashboardModal';
import { setCurrentTime, setMonitoredPeriod } from 'app/redux/features/dashboard/time-slice';
import {
    MapLayerUtils,
    MapObjectType,
    SolutionTvImpact,
    WeatherLayerUtils,
} from 'app/components/dashboard/network/map/Map';
import MapUtils from 'app/components/map/util/MapUtils';
import Utils from 'app/common/util/Utils';
import { DatasetType, HotspotDto, MonitoredLocationType } from 'app/model/CORE';
import { VALIDITY_PERIOD_PROPERTY_NAME } from 'app/components/map/ol/plugin/TvConvectionMapDisplayPlugin';
import { TV_PROPERTY_NAME } from 'app/components/map/ol/plugin/PolygonConvectionMapDisplayPlugin';
import { MapContextMenu, MapContextMenuProps } from 'app/components/dashboard/network/flows/menu/MapContextMenu';
import MeasureUtils from 'app/common/util/MeasureUtils';
import { FullscreenMapSettings } from 'app/components/dashboard/network/FullscreenMapView';
import ChatPanel from 'app/components/dashboard/chat/ChatPanel';
import { ModalHandler } from 'app/components/panels/ModalHandler';
import { ServiceFactory } from 'app/service/ServiceFactory';

const POLLING_REFRESH_INTERVAL_IN_MS = 5 * TimeUtils.SEC_TO_MS;
const MAX_ALLOWED_POLLING_COMMUNICATION_FAILURES = 20;
const mapStateToProps = (state: RootState) => {
    return {
        dashboardData: state.dashboardData.dashboardData,
        convectionData: state.dashboardData.convectionData,
        dataVersion: state.dashboardData.currentDataVersion,
        mapSettings: state.mapSettingsData.mapSettings,
        loadConvection: state.dashboardData.loadConvection,
        currentTime: state.timeData.currentTime,
        monitoredPeriod: state.timeData.monitoredPeriod,
        newChatMessages: state.dashboardData.newChatMessages,
        drawerOpen: state.dashboardData.drawerOpen,
    };
};

const mapDispatchToProps = {
    getDashboardData,
    getDashboardDataForCurrentVersion,
    getDataVersion,
    setDataVersion,
    setMapSettings,
    setCurrentTime,
    setMonitoredPeriod,
    setFullscreenMapSettings,
    setDrawerOpen,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface FmpMapDashboardState {
    loading: boolean;
}

type FmpMapDashboardProps = PropsFromRedux & {};

const FULLSCREEN_MAP_SETTINGS: FullscreenMapSettings = {
    accLayerEnabled: false,
    measureLayerEnabled: false,
    militaryLabelEnabled: false,
    natLayerEnabled: false,
    tfvLayerEnabled: false,
    weatherLayerEnabled: true,
    hotspotLayerEnabled: false,
    workspaceLayerEnabled: false,
    showLayerSelection: true,
    showFlightLevelBandSelection: true,
};

export class FmpMapDashboard extends React.PureComponent<FmpMapDashboardProps, FmpMapDashboardState> {
    private globalTimeSelectionPopupRef = createRef<GlobalTimeSelectionPopup>();

    constructor(props) {
        super(props);
        this.state = {
            loading: false,
        };
        this.props.setFullscreenMapSettings(FULLSCREEN_MAP_SETTINGS);
    }

    componentDidMount() {
        this.initialise();
    }

    componentDidUpdate(
        prevProps: Readonly<FmpMapDashboardProps>,
        prevState: Readonly<FmpMapDashboardState>,
        snapshot?: any,
    ) {
        if (prevProps.monitoredPeriod !== this.props.monitoredPeriod) {
            this.refreshDashboardData(() => this.setState({ loading: false }));
        } else if (prevProps.dataVersion !== this.props.dataVersion) {
            this.refreshDashboardData();
        }
    }

    refreshDashboardData = (callback?: () => void) => {
        const request: DashboardDataRequest = {
            // overlappingFilter: this.state.globalFilterEnabled ? this.getOverlappingFilter() : undefined,
            overlappingFilter: {
                selectedFlights: [],
                selectedMeasures: [],
                selectedTvs: [],
                selectedHotspotTvs: [],
                selectedEvents: [],
                eventsFilters: [],
                flightsFilters: [],
                measuresFilters: [],
                tvsFilters: [],
            },
            capacityModel: AppContext.getCapacityModel(),
            convectionSource: AppContext.getConvectionSource(),
            monitoredPeriod: AppContext.getMonitoringPeriod(),
        };
        this.props.getDashboardDataForCurrentVersion(request).then(() => {
            this.props.setCurrentTime(AppContext.getCurrentTime());
            if (callback) {
                callback();
            }
        });
    };

    initialise = () => {
        if (AppContext.configData) {
            this.props.getDataVersion();
            setInterval(() => {
                // Check if new data is available every 10s
                this.props.getDataVersion();
            }, 10000);
        }
        this.initEventListeners();
    };

    initEventListeners() {
        //app settings changed
        AppContext.eventBus.register(DashboardSettingsUpdateEvent.NAME, (event: DashboardSettingsUpdateEvent) => {
            const dashboardDataRefreshRequired =
                AppContext.dashboardSettings.capacityModel !== event.dashboardSettings.capacityModel ||
                AppContext.dashboardSettings.convectionSource !== event.dashboardSettings.convectionSource;
            AppContext.setDashboardSettings(event.dashboardSettings);
            if (dashboardDataRefreshRequired) {
                this.refreshDashboardData();
            }
        });
        //header time settings changed
        AppContext.eventBus.register(MonitoringPeriodSettingsChange.NAME, (event: MonitoringPeriodSettingsChange) => {
            const currentDataset = AppContext.getDataset();

            const deltaDay = event.monitoringPeriodSettings.deltaDay;
            if (deltaDay > 0 && AppContext.getDataset().type === DatasetType.OPERATIONAL) {
                AppContext.setDataset({ type: DatasetType.FORECAST });
            } else if (deltaDay < 1 && AppContext.getDataset().type === DatasetType.FORECAST) {
                AppContext.setDataset({ type: DatasetType.OPERATIONAL });
            }
            // Check data version before refreshing dashboard data,
            // Dataset may have changed so different server, different data version ID
            this.setState({ loading: true });
            ServiceFactory.sol49Service()
                .getDataVersion(AppContext.getDataset().type)
                .then(reply => {
                    if (reply.error) {
                        // Revert to old dataset ! (corresponding server not available)
                        AppContext.setDataset(currentDataset);
                        notification.error({ message: reply.error.message, description: reply.error.details });
                        console.error('getDataVersion failed:', reply.error);
                        this.setState({ loading: false });
                    } else {
                        AppContext.setMonitoringPeriodSettings(event.monitoringPeriodSettings);
                        this.props.setMonitoredPeriod(AppContext.getMonitoringPeriod());
                        this.props.setDataVersion(reply.data);
                    }
                })
                .catch(reason => {
                    // Revert to old dataset ! (corresponding server not available)
                    notification.error({
                        message: `${AppContext.getDataset().type} data not available`,
                        description: reason + '',
                        duration: 10,
                    });
                    AppContext.setDataset(currentDataset);
                    console.error('getDataVersion failed:', reason);
                    this.setState({ loading: false });
                });
        });
        //map right click
        AppContext.eventBus.register(MapElementClickEvent.NAME, (event: MapElementClickEvent) => {
            if (event.rightClick) {
                //only interested in the right click
                //tvs
                const tfvSelected = event.selection
                    .filter(element => element.type === MapObjectType.TV)
                    .map(mse => mse.subject);
                const extendedMonitoredTvs =
                    tfvSelected?.length > 0
                        ? this.props.dashboardData.monitoredTvList.filter(tv => tfvSelected.includes(tv.refLocation.id))
                        : [];
                const extendedMonitoredTvIds =
                    tfvSelected?.length > 0
                        ? this.props.dashboardData.monitoredTvList
                              .filter(tv => tfvSelected.includes(tv.refLocation.id))
                              .map(tv => tv.refLocation.id)
                        : [];

                //TODO remove once dashboard data reply has all tvs (including not active ones)
                //tvs from workspace might be ignored (not active and so not in the dashboard.monitoredTvList data)
                //so lets adding them so the right click still shows it but without load graph
                const workspaceTvs = event.selection.filter(
                    element =>
                        element.type === MapObjectType.TV &&
                        element.layerId === MapUtils.WORKSPACE_MAP_LAYER &&
                        !Utils.contains(extendedMonitoredTvIds, element.subject),
                );
                const workspaceExtendedMonitoredTvs = workspaceTvs.map(element => ({
                    refLocation: {
                        type: MonitoredLocationType.TV,
                        id: element.subject,
                    },
                }));
                if (workspaceExtendedMonitoredTvs?.length > 0) {
                    console.warn(
                        'Adding tvs to context menu from workspace layer. Not in the monitored data',
                        workspaceExtendedMonitoredTvs,
                    );
                    extendedMonitoredTvs.push(...workspaceExtendedMonitoredTvs);
                }

                //measures
                const measuresSelected = event.selection
                    .filter(element => element.type === MapObjectType.MEASURE)
                    .map(mse => mse.subject);
                const measures: Array<MonitoredMeasure> =
                    measuresSelected?.length > 0
                        ? this.props.dashboardData.monitoredMeasures.filter(measure =>
                              measuresSelected.includes(measure.measure.measureId.id),
                          )
                        : [];

                // hotspots
                const hotspotTvSelected = event.selection
                    .filter(element => element.type === MapObjectType.HOTSPOT)
                    .map(el => el.subject);
                const hotspotsSelected: { [p: string]: HotspotDto[] } =
                    hotspotTvSelected?.length > 0
                        ? Object.fromEntries(
                              Object.entries(this.props.dashboardData.hotspots).filter(([tv, _]) =>
                                  hotspotTvSelected.includes(tv),
                              ),
                          )
                        : {};

                const aerodromeIds = event.selection
                    .filter(element => element.type === MapObjectType.REGULATION_OUTLOOK)
                    .map(el => el.subject);
                const regOutlooks = this.props.dashboardData?.monitoredRegulationOutlook?.filter(mro =>
                    aerodromeIds?.includes(mro.regulationOutlook.aerodromeId),
                );

                const workspaceMeasures: Array<MonitoredMeasure> = event.selection
                    .filter(
                        element =>
                            element.type === MapObjectType.MEASURE && element.layerId === MapUtils.WORKSPACE_MAP_LAYER,
                    )
                    ?.map(mce => measures?.find(m => m.measure.measureId.id === mce.subject));

                //convection
                const mapConvectionElements = event.selection.filter(
                    element => element.type === MapObjectType.CONVECTION,
                );
                const tvMeteoForecast: TrafficVolumeMeteoForecast[] = mapConvectionElements
                    .map(element =>
                        WeatherLayerUtils.findTrafficVolumeMeteoForecast(
                            this.props.convectionData,
                            element.properties[VALIDITY_PERIOD_PROPERTY_NAME],
                            element.subject,
                        ),
                    )
                    .filter(obj => Utils.isExists(obj));
                const polygonMeteoForecast: PolygonMeteoForecast[] = mapConvectionElements
                    .map(element => {
                        if (Utils.isNotExists(element.properties[TV_PROPERTY_NAME])) {
                            return WeatherLayerUtils.findPolygonMeteoForecast(
                                this.props.convectionData,
                                element.subject,
                            );
                        } else {
                            const selectedConvection: PolygonMeteoForecast = WeatherLayerUtils.findPolygonMeteoForecast(
                                this.props.convectionData,
                                element.subject,
                            );
                            const tv: string = element.properties[TV_PROPERTY_NAME];
                            // Click on a TV inside a polygon convection, to display the info about this TV only, we filter out the impactedTvs
                            const tvAsp = selectedConvection.impactedTvs[tv];
                            if (tvAsp)
                                return Object.assign({}, selectedConvection, {
                                    impactedTvs: { [tv]: selectedConvection.impactedTvs[tv] },
                                });
                            else console.warn('missing tv airspace ref', tv, selectedConvection);
                            return null;
                        }
                    })
                    .filter(obj => Utils.isExists(obj)); //subject is unique
                //military zones
                const mapMilitaryZonesElements = event.selection.filter(
                    element => element.type === MapObjectType.MILITARY,
                );
                const militaryZones: MilitaryZoneDto[] = mapMilitaryZonesElements.map(element =>
                    MapLayerUtils.MILITARY_ZONES_CACHE.find(mz => mz.airspaceId === element.subject),
                );

                //show menu
                this.handleMapContextMenuSelection(
                    event.clientXY,
                    extendedMonitoredTvs,
                    measures,
                    hotspotsSelected,
                    tvMeteoForecast,
                    militaryZones,
                    polygonMeteoForecast,
                    workspaceExtendedMonitoredTvs,
                    workspaceMeasures,
                    null,
                    null,
                    regOutlooks,
                );
            }
        });
        //handles weather tv details click
        AppContext.eventBus.register(ShowTvDetailEvent.NAME, ({ tv }: ShowTvDetailEvent) => {
            const data = this.props.dashboardData as NetworkDashboardData;
            const vsName = data?.tvSetMap?.[tv];
            const monitoredMeasuresForTv = data.monitoredMeasures?.filter(
                monitoredMeasure => MeasureUtils.getTvLocation(monitoredMeasure.measure).id === tv,
            );
            const regulationPeriods = Utils.isNotEmpty(monitoredMeasuresForTv)
                ? monitoredMeasuresForTv.map(monitoredMeasure =>
                      MeasureUtils.getRegulationPeriods(monitoredMeasure.measure),
                  )
                : null;
            ModalHandler.showTvDetails(tv, vsName, regulationPeriods, undefined);
        });
    }

    render() {
        return (
            <Spin spinning={this.state.loading}>
                <Flex className={'dashboard'} direction="horizontal" onMouseMove={this.handleMainFlexMouseMove}>
                    <MapTrafficVolumeCacheComponent />
                    <MapPendingEventsComponent />
                    <SvgCommonDefs />
                    <DashboardModal />
                    <Flex direction="horizontal">
                        <Flex direction="vertical" size="12vw">
                            <Flex size="6em">
                                <Banner dataset={AppContext.getDataset()} />
                            </Flex>
                            <Flex size="4em">
                                <UserInfo userProfile={AppContext.configData?.userProfile} />
                            </Flex>
                            <ChatPanel
                                userId={AppContext.configData.claimedUserId}
                                pollingIntervalInMs={POLLING_REFRESH_INTERVAL_IN_MS}
                                pausePolling={false}
                                maxNumPollingFailures={MAX_ALLOWED_POLLING_COMMUNICATION_FAILURES}
                                onChatFailure={null}
                                onNewChat={null}
                            />
                        </Flex>
                        <Flex className="network-monitoring-panel" direction="vertical">
                            <Flex size="5vh" direction="vertical" style={{ alignItems: 'center' }}>
                                {this.renderHeader()}
                            </Flex>
                            <Flex>{this.renderMap()}</Flex>
                        </Flex>
                    </Flex>
                </Flex>
            </Spin>
        );
    }

    renderHeader() {
        return (
            <Flex size="20%" direction="vertical" className="network-monitoring-header">
                <GlobalTimeSelectionPopup
                    ref={this.globalTimeSelectionPopupRef}
                    onApply={this.handleMonitoringPeriodSettingsChange}>
                    <Flex direction="vertical" className="time-and-date" onClick={this.handleMonitoringFlexClick}>
                        <div style={{ marginTop: '0.25vw' }}>
                            {TimeUtils.formatDateDDMMYYYY(this.props.currentTime)}
                        </div>
                        <div style={{ marginTop: '0.5em' }}>
                            <span style={{ marginRight: '0.3em' }}>
                                {TimeUtils.formatHHMM(AppContext.getMonitoringPeriod()?.from)}
                            </span>
                            <span style={{ fontSize: '2em' }}>{TimeUtils.formatHHMM(this.props.currentTime)}</span>
                            <span style={{ marginLeft: '0.3em' }}>
                                {TimeUtils.formatHHMM(AppContext.getMonitoringPeriod()?.to)}
                            </span>
                        </div>
                    </Flex>
                </GlobalTimeSelectionPopup>
                <Flex style={{ justifyContent: 'center', marginTop: '0.5em' }}>
                    <MonitoredDaySelector
                        dataset={AppContext.getDataset().type}
                        value={TimeUtils.diffInDays(
                            AppContext.getMonitoredDay(),
                            TimeUtils.withoutTimeOfDay(TimeUtils.NOW()),
                        )}
                        onChange={this.handleMonitoredDayChange}
                    />
                </Flex>
            </Flex>
        );
    }

    renderMap() {
        const hotspots = this.props.dashboardData?.hotspots && Object.values(this.props.dashboardData?.hotspots).flat();
        return (
            // <Flex className='network-monitoring-header'>
            <Flex>
                <NetworkMonitoringMap
                    fullscreenMap={true}
                    currentTime={this.props.currentTime}
                    selectedTvs={[]}
                    selectedFlights={[]}
                    monitoredTvs={this.props.dashboardData?.monitoredTvList}
                    visible={true}
                    convectionData={this.props.convectionData}
                    // airspaceDef={this.props.convectionAirspaceDef}
                    mapSettings={this.props.mapSettings}
                    onMapSettingsChanged={this.props.setMapSettings}
                    monitoredPeriod={this.props.monitoredPeriod}
                    monitoredMeasures={this.props.dashboardData?.monitoredMeasures}
                    hotspots={hotspots}
                    selectedMeasures={[]}
                    textFilter={undefined}
                    tvSetMap={{}}
                    dashboardDataVersion={this.props.dashboardData?.version}
                    filteredTvIds={[]}
                    selectedHotspots={[]}
                    regulationOutlook={[]}
                    selectedRegulationOutlook={[]}
                />
            </Flex>
        );
    }

    //HANDLERS
    handleMenuDrawerOnClose = () => {
        this.props.setDrawerOpen(false);
    };

    handleMainFlexMouseMove = event => {
        if (event.pageX > window.outerWidth - 10 && !this.props.drawerOpen) {
            this.props.setDrawerOpen(true);
        }
    };

    handleMonitoringFlexClick = () => {
        this.globalTimeSelectionPopupRef.current.show();
    };

    handleMonitoringPeriodSettingsChange = (monitoringPeriodSettings: MonitoringPeriodSettings) => {
        MonitoringPeriodSettingsChange.fireEvent(monitoringPeriodSettings);
    };

    handleMonitoredDayChange = (deltaDay: number) => {
        this.handleMonitoringPeriodSettingsChange({ ...AppContext.monitoringPeriodSettings, deltaDay });
    };

    handleMapContextMenuSelection = (
        position: number[],
        tvs: Array<ExtendedMonitoredTv>,
        measures: Array<MonitoredMeasure>,
        hotspots: { [p: string]: HotspotDto[] },
        tvConvections: Array<TrafficVolumeMeteoForecast>,
        militaryZones: Array<MilitaryZoneDto>,
        polygonConvection: PolygonMeteoForecast[],
        workspaceTvs: Array<ExtendedMonitoredTv>,
        workspaceMeasures: Array<MonitoredMeasure>,
        tvImpacts: Array<SolutionTvImpact>,
        measureImpacts: Array<NiaMeasureImpact>,
        regOutlooks: Array<MonitoredRegulationOutlook>,
    ): void => {
        const convectionTvNames: Array<string> = [];
        tvConvections && convectionTvNames.push(...tvConvections.map(c => c.id));
        polygonConvection && convectionTvNames.push(...polygonConvection.flatMap(c => Object.keys(c.impactedTvs)));

        const mapContextMenuProps: MapContextMenuProps = {
            tvs: tvs,
            workspaceTvs: workspaceTvs,
            measures: measures,
            hotspots: hotspots,
            workspaceMeasures: workspaceMeasures,
            selectedFlightsOccupancyPeriods: {},
            currentTime: this.props.currentTime,
            selectedTvs: [],
            proposalMeasureIds: MeasureUtils.getProposalMeasureIds(this.props.dashboardData?.monitoredMcdmMeasures),
            monitoredMeasures: this.props.dashboardData?.monitoredMeasures,
            monitoredTvList: this.props.dashboardData?.monitoredTvList,
            tvSetMap: (this.props.dashboardData as NetworkDashboardData)?.tvSetMap,
            tvConvections: tvConvections,
            convectionTvs: this.props.dashboardData?.monitoredTvList.filter(tv =>
                convectionTvNames.includes(tv.refLocation.id),
            ),
            militaryZones: militaryZones,
            polygonConvections: polygonConvection,
            impactedTvs: tvImpacts,
            impactedMeasures: measureImpacts,
            events: [],
            regOutlooks: regOutlooks,
            meteoAlerts: this.props.dashboardData?.tvMeteoAlerts,
        };
        const modalContent = <MapContextMenu {...mapContextMenuProps} />;
        AppContext.eventBus.publish(
            new ShowModalEvent(
                ShowModalEvent.NAME,
                modalContent,
                MapContextMenu.hasOnlyOneKindOfMenu(mapContextMenuProps)
                    ? MapContextMenu.MAP_CONTEXT_MENU_SINGLE_WIDTH_EM
                    : MapContextMenu.MAP_CONTEXT_MENU_GROUP_WIDTH_EM,
                position[0],
                position[1],
            ),
        );
    };

    handleChatIconClick = () => {
        this.props.setDrawerOpen(!this.props.drawerOpen);
    };
}

export default connector(FmpMapDashboard);
